import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Typography,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Add, Edit, Delete, Visibility } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { statusColor, statusLabel } from '../../lib/deviceStatus'
import { PageHeader } from '../../components/PageHeader'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { TimezoneSelect } from '../../components/TimezoneSelect'
import {
  useDevices,
  useCreateDevice,
  useUpdateDevice,
  useDeleteDevice,
  Device,
  DEVICE_TYPES,
  VEHICLE_TYPES,
  DAY_NAMES,
  TrackingSchedule,
  DEFAULT_SCHEDULE,
} from '../../hooks/useDevices'
import { MenuItem, ToggleButton, ToggleButtonGroup, FormControlLabel, Switch, Stack } from '@mui/material'
import { format } from 'date-fns'

const emptyForm = {
  name: '', device_identifier: '', timezone: 'UTC', device_type: '', phone_number: '',
  vehicle_type: '', plate_number: '', owner_name: '', trip_idle_threshold_min: 30,
  tracking_schedule: { mode: 'always' } as TrackingSchedule,
}

export default function DevicesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: devices = [], isLoading } = useDevices()
  const createDevice = useCreateDevice()
  const updateDevice = useUpdateDevice()
  const deleteDevice = useDeleteDevice()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Device | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEdit = (d: Device) => {
    setEditing(d)
    setForm({ name: d.name, device_identifier: d.device_identifier, timezone: d.timezone || 'UTC', device_type: d.device_type || '', phone_number: d.phone_number || '', vehicle_type: d.vehicle_type || '', plate_number: d.plate_number || '', owner_name: d.owner_name || '', trip_idle_threshold_min: d.trip_idle_threshold_min ?? 30, tracking_schedule: d.tracking_schedule ?? DEFAULT_SCHEDULE })
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (editing) {
      await updateDevice.mutateAsync({ id: editing.id, name: form.name, timezone: form.timezone, device_type: form.device_type, phone_number: form.phone_number, vehicle_type: form.vehicle_type, plate_number: form.plate_number, owner_name: form.owner_name, trip_idle_threshold_min: form.trip_idle_threshold_min, tracking_schedule: form.tracking_schedule } as any)
      analytics.track(EVENTS.DEVICE_UPDATED, { device_id: editing.id, device_type: form.device_type })
    } else {
      await createDevice.mutateAsync(form)
      analytics.track(EVENTS.DEVICE_REGISTERED, { device_type: form.device_type, vehicle_type: form.vehicle_type })
    }
    setDialogOpen(false)
  }

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160 },
    { field: 'device_identifier', headerName: t('device.identifier'), flex: 1, minWidth: 160 },
    { field: 'device_type', headerName: t('common.type'), width: 120,
      renderCell: ({ value }) => value ? <Chip label={value} size="small" variant="outlined" /> : <Typography variant="caption" color="text.disabled">—</Typography> },
    { field: 'vehicle_type', headerName: t('device.vehicleType'), width: 120,
      renderCell: ({ value }) => value ? <Chip label={value} size="small" variant="outlined" /> : <Typography variant="caption" color="text.disabled">—</Typography> },
    { field: 'plate_number', headerName: t('device.plateNumber'), width: 130 },
    { field: 'owner_name', headerName: t('device.ownerName'), width: 150 },
    {
      field: 'status',
      headerName: t('common.status'),
      width: 120,
      renderCell: ({ value }) => {
        const s = (value as string) ?? 'offline'
        return (
          <Chip
            label={statusLabel(s)}
            size="small"
            sx={{
              bgcolor: statusColor(s) + '18',
              color: statusColor(s),
              fontWeight: 600,
              border: `1px solid ${statusColor(s)}33`,
            }}
          />
        )
      },
    },
    {
      field: 'created_at',
      headerName: t('device.created'),
      width: 150,
      valueFormatter: (v: string) => (v ? format(new Date(v), 'PP') : '—'),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title={t('device.info')}>
            <IconButton size="small" onClick={() => { navigate(`/devices/${row.id}`) }}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <IconButton size="small" onClick={() => openEdit(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title={t('device.title')}
        subtitle={t('device.subtitle')}
        breadcrumbs={[{ label: t('device.title') }]}
        action={
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            {t('device.register')}
          </Button>
        }
      />

      {createDevice.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('device.failedToRegister')}
        </Alert>
      )}

      <DataGrid
        rows={devices}
        columns={columns}
        loading={isLoading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? t('device.editDevice') : t('device.registerDevice')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label={t('common.name')}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label={t('device.identifier')}
            value={form.device_identifier}
            onChange={(e) => setForm((p) => ({ ...p, device_identifier: e.target.value }))}
            required={!editing}
            disabled={!!editing}
            fullWidth
            helperText={editing ? t('device.cannotChangeId') : t('device.idHelperText')}
          />
          <TextField
            select
            label={t('device.deviceType')}
            value={form.device_type}
            onChange={(e) => setForm((p) => ({ ...p, device_type: e.target.value }))}
            fullWidth
          >
            <MenuItem value="">{t('device.none')}</MenuItem>
            {DEVICE_TYPES.map((type) => <MenuItem key={type} value={type}>{type.replace(/_/g, ' ')}</MenuItem>)}
          </TextField>
          <TextField
            select
            label={t('device.vehicleType')}
            value={form.vehicle_type}
            onChange={(e) => setForm((p) => ({ ...p, vehicle_type: e.target.value }))}
            fullWidth
          >
            <MenuItem value="">{t('device.none')}</MenuItem>
            {VEHICLE_TYPES.map((type) => <MenuItem key={type} value={type}>{type.replace(/_/g, ' ')}</MenuItem>)}
          </TextField>
          <TextField
            label={t('device.plateNumber')}
            value={form.plate_number}
            onChange={(e) => setForm((p) => ({ ...p, plate_number: e.target.value }))}
            fullWidth
            placeholder="e.g. 12345 A B"
          />
          <TextField
            label={t('device.ownerName')}
            value={form.owner_name}
            onChange={(e) => setForm((p) => ({ ...p, owner_name: e.target.value }))}
            fullWidth
            placeholder={t('device.ownerPlaceholder')}
          />
          <TextField
            label={t('device.phoneNumber')}
            value={form.phone_number}
            onChange={(e) => setForm((p) => ({ ...p, phone_number: e.target.value }))}
            fullWidth
            placeholder="+964..."
          />
          <TextField
            label={t('device.tripIdleThreshold')}
            type="number"
            value={form.trip_idle_threshold_min}
            onChange={(e) => setForm((p) => ({ ...p, trip_idle_threshold_min: Math.max(1, Math.min(1440, Number(e.target.value) || 30)) }))}
            fullWidth
            helperText={t('device.tripIdleHelperText')}
            inputProps={{ min: 1, max: 1440 }}
          />
          <TimezoneSelect value={form.timezone} onChange={(tz) => setForm((p) => ({ ...p, timezone: tz }))} />

          {/* Tracking Schedule */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.tracking_schedule.mode === 'scheduled'}
                  onChange={(e) => setForm((p) => ({
                    ...p,
                    tracking_schedule: e.target.checked
                      ? { mode: 'scheduled', days: [1,2,3,4,5], start_time: '08:00', end_time: '18:00' }
                      : { mode: 'always' },
                  }))}
                />
              }
              label={t('device.customTrackingSchedule')}
            />
            {form.tracking_schedule.mode === 'scheduled' && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{t('device.activeTrackingDays')}</Typography>
                <ToggleButtonGroup
                  value={form.tracking_schedule.days ?? []}
                  onChange={(_, days) => setForm((p) => ({
                    ...p,
                    tracking_schedule: { ...p.tracking_schedule, days },
                  }))}
                  size="small"
                >
                  {DAY_NAMES.map((day, i) => (
                    <ToggleButton key={i} value={i} sx={{ px: 1.5 }}>{day}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label={t('device.startTime')} type="time" size="small" fullWidth
                    value={form.tracking_schedule.start_time ?? '08:00'}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      tracking_schedule: { ...p.tracking_schedule, start_time: e.target.value },
                    }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label={t('device.endTime')} type="time" size="small" fullWidth
                    value={form.tracking_schedule.end_time ?? '18:00'}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      tracking_schedule: { ...p.tracking_schedule, end_time: e.target.value },
                    }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createDevice.isPending || updateDevice.isPending || !form.name}
          >
            {editing ? t('common.update') : t('device.register')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('common.delete')}
        message={t('device.confirmDelete')}
        onConfirm={() => {
          analytics.track(EVENTS.DEVICE_DELETED, { device_id: deleteTarget! })
          deleteDevice.mutate(deleteTarget!)
          setDeleteTarget(null)
        }}
        onClose={() => setDeleteTarget(null)}
        loading={deleteDevice.isPending}
      />
    </Box>
  )
}
